import type { ConsecutiveGroup } from '@/utils/collection-util/collectionUtil.types'

/**
 * Narrows an index response body to its rows.
 *
 * An index call with `page=-1` answers with a bare array. Any other page answers with the
 * pagination envelope, `{ data: [...] }`. A failed call leaves the body empty. All three read as a list.
 */
export const unwrapCollection = <TItem>(body: TItem[] | { data?: TItem[] } | null | undefined): TItem[] =>
  Array.isArray(body) ? body : (body?.data ?? [])

/**
 * Folds neighbouring items with an equal key into one group, keeping the input order. Only
 * neighbours fold, so a key that comes back later starts a new group. Sort first to group by key.
 */
export const groupConsecutive = <TItem, TKey>(
  items: readonly TItem[],
  keyOf: (item: TItem) => TKey
): ConsecutiveGroup<TKey, TItem>[] =>
  items.reduce<ConsecutiveGroup<TKey, TItem>[]>((groups, item) => {
    const key = keyOf(item)
    const last = groups.at(-1)

    if (last && last.key === key) {
      last.items.push(item)
      return groups
    }

    groups.push({ key, items: [item] })
    return groups
  }, [])
